import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Type, Square, Save, Trash2, Move, Maximize2, ImagePlus, X, Edit3, Check, DollarSign, BringToFront, SendToBack, ChevronUp, ChevronDown, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  { value: '#1A3C34', label: 'Vert fonce' },
  { value: '#C77D63', label: 'Terracotta' },
  { value: '#4A90D9', label: 'Bleu' },
  { value: '#E8D44D', label: 'Jaune' },
  { value: '#7B68EE', label: 'Violet' },
  { value: '#E2E2DF', label: 'Gris clair' },
  { value: '#F0EFEA', label: 'Beige' },
  { value: '#FFFFFF', label: 'Blanc' },
];

const TEXT_COLORS = [
  { value: 'auto', label: 'Automatique' },
  { value: '#1A3C34', label: 'Vert fonce' },
  { value: '#C77D63', label: 'Terracotta' },
  { value: '#FFFFFF', label: 'Blanc' },
  { value: '#000000', label: 'Noir' },
  { value: '#737373', label: 'Gris' },
  { value: '#4A90D9', label: 'Bleu' },
  { value: '#E8D44D', label: 'Jaune' },
];

export default function FloorPlanEditor({ spaces, getHeaders, onSpacesChanged }) {
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSpaceManager, setShowSpaceManager] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [newSpacePrice, setNewSpacePrice] = useState('10');
  const [newSpacePriceDaily, setNewSpacePriceDaily] = useState('60');
  const [newSpacePriceWeekly, setNewSpacePriceWeekly] = useState('240');
  const [newSpaceCapacity, setNewSpaceCapacity] = useState('1');
  const [editingSpaceName, setEditingSpaceName] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [editingSpacePrices, setEditingSpacePrices] = useState(null);
  const [editPriceValues, setEditPriceValues] = useState({});
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [editCapacityValue, setEditCapacityValue] = useState('');
  const [savingSpace, setSavingSpace] = useState(false);
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasW = 900;
  const canvasH = 550;

  const fetchPlan = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/floor-plan`);
      if (res.data.length > 0) {
        setElements(res.data);
      } else {
        setElements([
          { element_id: 'el_1', type: 'space', x: 20, y: 20, width: 340, height: 220, text: 'Grande Salle', color: '#1A3C34', linked_space_id: 'grande-salle', font_size: 16, image_url: null },
          { element_id: 'el_2', type: 'space', x: 380, y: 20, width: 180, height: 120, text: 'Salle de Reunion', color: '#4A90D9', linked_space_id: 'salle-reunion', font_size: 13, image_url: null },
          { element_id: 'el_3', type: 'space', x: 380, y: 160, width: 90, height: 80, text: 'Table 2', color: '#C77D63', linked_space_id: 'table-2', font_size: 12, image_url: null },
          { element_id: 'el_4', type: 'space', x: 490, y: 160, width: 90, height: 80, text: 'Table 3', color: '#C77D63', linked_space_id: 'table-3', font_size: 12, image_url: null },
          { element_id: 'el_5', type: 'space', x: 600, y: 20, width: 180, height: 220, text: 'Bureau', color: '#7B68EE', linked_space_id: 'bureau', font_size: 14, image_url: null },
          { element_id: 'el_6', type: 'label', x: 20, y: 270, width: 120, height: 40, text: 'Entree', color: '#E2E2DF', font_size: 14, image_url: null },
          { element_id: 'el_7', type: 'label', x: 700, y: 270, width: 100, height: 40, text: 'Toilettes', color: '#E2E2DF', font_size: 12, image_url: null },
        ]);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Detect plan rectangles that look like spaces but aren't linked to any reservable space.
  // Returns the list of orphan elements (type=space, no linked_space_id, text != default placeholder).
  const findOrphanRectangles = () => {
    return elements.filter(el =>
      el.type === 'space' &&
      !el.linked_space_id &&
      (el.text || '').trim() &&
      (el.text || '').trim().toLowerCase() !== 'nouvel espace'
    );
  };

  // Auto-create reservable spaces for orphan rectangles, then link them.
  // Returns the updated elements array (caller responsible for using it).
  const autoCreateSpacesForOrphans = async (orphans) => {
    let updated = [...elements];
    for (const orphan of orphans) {
      try {
        const res = await axios.post(`${API}/admin/spaces`, {
          name: orphan.text.trim(),
          price_hourly: 10,
          price_daily: 60,
          price_weekly: 240,
          capacity: 1,
        }, {
          headers: { ...getHeaders(), 'Content-Type': 'application/json' },
          withCredentials: true
        });
        const newId = res.data.space_id;
        updated = updated.map(el =>
          el.element_id === orphan.element_id ? { ...el, linked_space_id: newId } : el
        );
        toast.success(`Espace "${orphan.text}" cree automatiquement (10 EUR/h)`);
      } catch (err) {
        toast.error(`Impossible de creer "${orphan.text}": ${err.response?.data?.detail || 'erreur'}`);
      }
    }
    return updated;
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      let toSave = elements;

      // Auto-detect orphan rectangles & propose auto-creation
      const orphans = findOrphanRectangles();
      if (orphans.length > 0) {
        const names = orphans.map(o => `"${o.text}"`).join(', ');
        const ok = window.confirm(
          `${orphans.length} rectangle(s) (${names}) n'est/ne sont lie(s) a aucun espace reservable.\n\n` +
          `Voulez-vous creer automatiquement ces espaces reservables (10 EUR/h, 60 EUR/jour par defaut) ?\n\n` +
          `OK = creer + sauvegarder    |    Annuler = sauvegarder uniquement le plan visuel`
        );
        if (ok) {
          toSave = await autoCreateSpacesForOrphans(orphans);
          setElements(toSave);
          if (onSpacesChanged) onSpacesChanged();
        }
      }

      await axios.post(`${API}/admin/floor-plan`, { elements: toSave }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      toast.success('Plan sauvegarde avec succes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addSpace = () => {
    const id = `el_${Date.now()}`;
    setElements(prev => [...prev, {
      element_id: id, type: 'space', x: 100, y: 100, width: 140, height: 100,
      text: 'Nouvel espace', color: '#1A3C34', linked_space_id: null, font_size: 13, image_url: null
    }]);
    setSelected(id);
  };

  const addLabel = () => {
    const id = `el_${Date.now()}`;
    setElements(prev => [...prev, {
      element_id: id, type: 'label', x: 100, y: 100, width: 100, height: 35,
      text: 'Texte', color: '#E2E2DF', font_size: 13, image_url: null
    }]);
    setSelected(id);
  };

  const deleteSelected = () => {
    if (!selected) return;
    setElements(prev => prev.filter(e => e.element_id !== selected));
    setSelected(null);
  };

  // Z-order: array order determines SVG render order (later = on top)
  const moveZ = (direction) => {
    if (!selected) return;
    setElements(prev => {
      const idx = prev.findIndex(e => e.element_id === selected);
      if (idx === -1) return prev;
      const arr = [...prev];
      const [item] = arr.splice(idx, 1);
      if (direction === 'front') arr.push(item);
      else if (direction === 'back') arr.unshift(item);
      else if (direction === 'forward') arr.splice(Math.min(idx + 1, arr.length), 0, item);
      else if (direction === 'backward') arr.splice(Math.max(idx - 1, 0), 0, item);
      return arr;
    });
  };

  const updateElement = (field, value) => {
    if (!selected) return;
    setElements(prev => prev.map(e =>
      e.element_id === selected ? { ...e, [field]: value } : e
    ));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      updateElement('image_url', res.data.image_url);
      toast.success('Image ajoutee');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    updateElement('image_url', null);
  };

  // Space management functions
  const addNewSpace = async () => {
    if (!newSpaceName.trim()) return;
    setSavingSpace(true);
    try {
      const res = await axios.post(`${API}/admin/spaces`, {
        name: newSpaceName.trim(),
        price_hourly: parseFloat(newSpacePrice) || 10,
        price_daily: parseFloat(newSpacePriceDaily) || 60,
        price_weekly: parseFloat(newSpacePriceWeekly) || 240,
        capacity: parseInt(newSpaceCapacity) || 1,
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      // Auto-create a plan element linked to this space
      const newSpaceId = res.data.space_id;
      const id = `el_${Date.now()}`;
      setElements(prev => [...prev, {
        element_id: id, type: 'space', x: 100, y: 100, width: 140, height: 100,
        text: newSpaceName.trim(), color: '#1A3C34', linked_space_id: newSpaceId, font_size: 13, image_url: null
      }]);
      setSelected(id);
      toast.success(`Espace "${newSpaceName}" cree et ajoute au plan`);
      setNewSpaceName('');
      setNewSpacePrice('10');
      setNewSpacePriceDaily('60');
      setNewSpacePriceWeekly('240');
      setNewSpaceCapacity('1');
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation espace');
    } finally {
      setSavingSpace(false);
    }
  };

  const startEditPrices = (space) => {
    setEditingSpacePrices(space.space_id);
    setEditPriceValues({
      price_hourly: space.price_hourly,
      price_daily: space.price_daily,
      price_weekly: space.price_weekly,
      capacity: space.capacity || 1,
    });
  };

  const savePrices = async (spaceId) => {
    setSavingSpace(true);
    try {
      await axios.put(`${API}/admin/spaces/${spaceId}`, {
        price_hourly: parseFloat(editPriceValues.price_hourly),
        price_daily: parseFloat(editPriceValues.price_daily),
        price_weekly: parseFloat(editPriceValues.price_weekly),
        capacity: parseInt(editPriceValues.capacity),
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      toast.success('Tarifs mis a jour');
      setEditingSpacePrices(null);
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingSpace(false);
    }
  };

  const renameSpace = async (spaceId) => {
    if (!editingNameValue.trim()) return;
    setSavingSpace(true);
    try {
      await axios.put(`${API}/admin/spaces/${spaceId}`, { name: editingNameValue.trim() }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      toast.success('Nom modifie');
      setEditingSpaceName(null);
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur modification');
    } finally {
      setSavingSpace(false);
    }
  };

  const saveCapacity = async (spaceId) => {
    const cap = parseInt(editCapacityValue, 10);
    if (Number.isNaN(cap) || cap < 1) { toast.error('Capacite invalide'); return; }
    setSavingSpace(true);
    try {
      await axios.put(`${API}/admin/spaces/${spaceId}`, { capacity: cap }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      toast.success(`Capacite mise a jour (${cap} pers.)`);
      setEditingCapacity(null);
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingSpace(false);
    }
  };

  const deleteSpace = async (spaceId) => {
    const spaceName = spaces.find(s => s.space_id === spaceId)?.name || spaceId;
    if (!window.confirm(`Supprimer l'espace "${spaceName}" ? Cette action est irreversible.`)) return;
    setSavingSpace(true);
    try {
      await axios.delete(`${API}/admin/spaces/${spaceId}`, {
        headers: getHeaders(), withCredentials: true
      });
      // Unlink from any floor plan elements
      setElements(prev => prev.map(el =>
        el.linked_space_id === spaceId ? { ...el, linked_space_id: null } : el
      ));
      toast.success(`Espace "${spaceName}" supprime`);
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally {
      setSavingSpace(false);
    }
  };

  const getSvgPoint = (e) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvasW,
      y: ((e.clientY - rect.top) / rect.height) * canvasH
    };
  };

  const handleMouseDown = (e, elId, action) => {
    e.stopPropagation();
    e.preventDefault();
    setSelected(elId);
    const pt = getSvgPoint(e);
    const el = elements.find(x => x.element_id === elId);
    if (action === 'move') {
      setDragging({ id: elId, offX: pt.x - el.x, offY: pt.y - el.y });
    } else if (action === 'resize') {
      setResizing({ id: elId, startX: pt.x, startY: pt.y, origW: el.width, origH: el.height });
    }
  };

  const handleMouseMove = (e) => {
    const pt = getSvgPoint(e);
    if (dragging) {
      const nx = Math.max(0, Math.min(canvasW - 20, pt.x - dragging.offX));
      const ny = Math.max(0, Math.min(canvasH - 20, pt.y - dragging.offY));
      setElements(prev => prev.map(el =>
        el.element_id === dragging.id ? { ...el, x: Math.round(nx), y: Math.round(ny) } : el
      ));
    } else if (resizing) {
      const dw = pt.x - resizing.startX;
      const dh = pt.y - resizing.startY;
      const nw = Math.max(40, resizing.origW + dw);
      const nh = Math.max(25, resizing.origH + dh);
      setElements(prev => prev.map(el =>
        el.element_id === resizing.id ? { ...el, width: Math.round(nw), height: Math.round(nh) } : el
      ));
    }
  };

  const handleMouseUp = () => {
    setDragging(null);
    setResizing(null);
  };

  const selectedEl = elements.find(e => e.element_id === selected);

  return (
    <div className="space-y-4" data-testid="floor-plan-editor">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={addSpace} className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-8 text-xs" data-testid="add-space-btn">
          <Square className="w-3 h-3 mr-1" /> Ajouter espace
        </Button>
        <Button size="sm" onClick={addLabel} variant="outline" className="border-[#E2E2DF] h-8 text-xs" data-testid="add-label-btn">
          <Type className="w-3 h-3 mr-1" /> Ajouter texte
        </Button>
        {selected && (
          <Button size="sm" onClick={deleteSelected} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs" data-testid="delete-element-btn">
            <Trash2 className="w-3 h-3 mr-1" /> Supprimer
          </Button>
        )}
        {selected && (
          <div className="flex items-center gap-1 pl-2 ml-1 border-l border-[#E2E2DF]" data-testid="z-order-controls">
            <Button size="sm" onClick={() => moveZ('front')} variant="outline" className="border-[#E2E2DF] h-8 px-2 text-xs" title="Premier plan" data-testid="z-front-btn">
              <BringToFront className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={() => moveZ('forward')} variant="outline" className="border-[#E2E2DF] h-8 px-2 text-xs" title="Avancer d'un cran" data-testid="z-forward-btn">
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={() => moveZ('backward')} variant="outline" className="border-[#E2E2DF] h-8 px-2 text-xs" title="Reculer d'un cran" data-testid="z-backward-btn">
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={() => moveZ('back')} variant="outline" className="border-[#E2E2DF] h-8 px-2 text-xs" title="Arriere-plan" data-testid="z-back-btn">
              <SendToBack className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        <div className="ml-auto">
          <Button size="sm" onClick={savePlan} disabled={saving} className="bg-green-600 text-white hover:bg-green-700 h-8 text-xs" data-testid="save-plan-btn">
            <Save className="w-3 h-3 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder le plan'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* SVG Canvas */}
        <div className="border border-[#E2E2DF] rounded-md bg-white overflow-hidden">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${canvasW} ${canvasH}`}
            className="w-full h-auto cursor-crosshair select-none"
            style={{ maxHeight: '550px' }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={() => setSelected(null)}
          >
            {/* Grid */}
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width={canvasW} height={canvasH} fill="url(#grid)" />

            {elements.map(el => {
              const isSelected = el.element_id === selected;
              const isSpace = el.type === 'space';
              const hasImage = !!el.image_url;
              return (
                <g key={el.element_id}>
                  {/* Clip path for image */}
                  {hasImage && (
                    <defs>
                      <clipPath id={`clip-${el.element_id}`}>
                        <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={4} />
                      </clipPath>
                    </defs>
                  )}
                  {/* Background rect */}
                  <rect
                    x={el.x}
                    y={el.y}
                    width={el.width}
                    height={el.height}
                    rx={4}
                    fill={hasImage ? '#FFFFFF' : (isSpace ? el.color + '22' : el.color + '44')}
                    stroke={isSelected ? '#C77D63' : el.color}
                    strokeWidth={isSelected ? 2.5 : 1.5}
                    strokeDasharray={isSpace ? 'none' : '5,3'}
                    style={{ cursor: 'move' }}
                    onMouseDown={(e) => handleMouseDown(e, el.element_id, 'move')}
                    onClick={(e) => { e.stopPropagation(); setSelected(el.element_id); }}
                  />
                  {/* Image if present */}
                  {hasImage && (
                    <image
                      href={el.image_url}
                      x={el.x}
                      y={el.y}
                      width={el.width}
                      height={el.height}
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#clip-${el.element_id})`}
                      opacity={1}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  {/* Text label */}
                  <text
                    x={el.x + el.width / 2}
                    y={el.y + el.height / 2 - (isSpace && el.linked_space_id && el.show_price !== false ? 7 : 0)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={el.font_size || 13}
                    fontWeight={isSpace ? '600' : '400'}
                    fill={el.text_color && el.text_color !== 'auto' ? el.text_color : (hasImage ? '#FFFFFF' : (isSpace ? el.color : '#737373'))}
                    style={{ pointerEvents: 'none', userSelect: 'none', textShadow: hasImage ? '0 1px 3px rgba(0,0,0,0.7)' : 'none', paintOrder: 'stroke', stroke: hasImage ? 'rgba(0,0,0,0.55)' : 'none', strokeWidth: hasImage ? 3 : 0, strokeLinejoin: 'round' }}
                  >
                    {el.text}
                  </text>
                  {/* Price subtitle */}
                  {isSpace && el.linked_space_id && el.show_price !== false && (
                    <text
                      x={el.x + el.width / 2}
                      y={el.y + el.height / 2 + (el.font_size || 13) - 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={10}
                      fill={el.text_color && el.text_color !== 'auto' ? el.text_color : (hasImage ? '#FFFFFF' : '#737373')}
                      style={{ pointerEvents: 'none', userSelect: 'none', textShadow: hasImage ? '0 1px 3px rgba(0,0,0,0.7)' : 'none', paintOrder: 'stroke', stroke: hasImage ? 'rgba(0,0,0,0.55)' : 'none', strokeWidth: hasImage ? 2.5 : 0, strokeLinejoin: 'round' }}
                    >
                      {spaces.find(s => s.space_id === el.linked_space_id)?.price_hourly || ''} EUR/h
                    </text>
                  )}
                  {/* Resize handle */}
                  {isSelected && (
                    <rect
                      x={el.x + el.width - 8}
                      y={el.y + el.height - 8}
                      width={10}
                      height={10}
                      fill="#C77D63"
                      rx={2}
                      style={{ cursor: 'se-resize' }}
                      onMouseDown={(e) => handleMouseDown(e, el.element_id, 'resize')}
                    />
                  )}
                  {/* Orphan-space warning badge: visible directly on the canvas if a 'space'
                      rectangle is not linked to any reservable space — helps the admin spot
                      misconfigured rectangles BEFORE saving. */}
                  {isSpace && !el.linked_space_id && (
                    <g style={{ pointerEvents: 'none' }}>
                      <rect
                        x={el.x + 4}
                        y={el.y + 4}
                        width={28}
                        height={16}
                        rx={3}
                        fill="#DC2626"
                      />
                      <text
                        x={el.x + 18}
                        y={el.y + 12}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={9}
                        fontWeight="700"
                        fill="#FFFFFF"
                      >
                        ⚠ vide
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Properties panel */}
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3 max-h-[580px] overflow-y-auto">
          <h4 className="font-body text-sm font-semibold text-[#1A3C34]">
            {selectedEl ? 'Proprietes' : 'Selectionnez un element'}
          </h4>

          {selectedEl && (
            <>
              {/* Text — what is displayed INSIDE the rectangle on the floor plan */}
              <div>
                <label className="text-xs font-semibold text-[#1A3C34]">Texte (affiche dans le carre)</label>
                <Input
                  value={selectedEl.text}
                  onChange={(e) => updateElement('text', e.target.value)}
                  className="h-8 text-sm border-[#E2E2DF] mt-1"
                  data-testid="editor-text-input"
                  placeholder="Ex: essai1, Studio A, Box 3..."
                />
                <p className="text-[10px] text-[#737373] mt-0.5 italic">Nom visible sur le plan public</p>
              </div>

              {/* Linked space — the actual reservable space (price, capacity) shown in the booking side-panel.
                  Independent of the on-plan text label. */}
              {selectedEl.type === 'space' && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#1A3C34]">Espace lie</label>
                    <button
                      className="text-[11px] font-semibold text-white bg-[#C77D63] hover:bg-[#b06a51] px-2 py-1 rounded transition-colors"
                      onClick={() => setShowSpaceManager(!showSpaceManager)}
                      data-testid="toggle-space-manager"
                    >
                      {showSpaceManager ? '× Fermer' : '+ Gerer les espaces'}
                    </button>
                  </div>
                  <Select
                    value={selectedEl.linked_space_id || 'none'}
                    onValueChange={(v) => updateElement('linked_space_id', v === 'none' ? null : v)}
                  >
                    <SelectTrigger className="h-8 text-sm border-[#E2E2DF] mt-1" data-testid="editor-linked-space">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {spaces.map(s => (
                        <SelectItem key={s.space_id} value={s.space_id}>{s.name} ({s.price_hourly} EUR/h)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Space Manager */}
                  {showSpaceManager && (
                    <div className="mt-2 border border-[#E2E2DF] rounded-md p-2 bg-[#F9F9F7] space-y-2" data-testid="space-manager">
                      <p className="text-[10px] font-semibold text-[#1A3C34]">Gestion des espaces</p>
                      {/* Existing spaces - rename/delete/edit prices */}
                      <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                        {spaces.map(s => (
                          <div key={s.space_id} className="border-b border-[#E2E2DF] pb-1.5 last:border-0" data-testid={`manage-space-${s.space_id}`}>
                            {/* Name row */}
                            <div className="flex items-center gap-1">
                              {editingSpaceName === s.space_id ? (
                                <>
                                  <Input
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    className="h-6 text-[11px] border-[#E2E2DF] flex-1"
                                    onKeyDown={(e) => e.key === 'Enter' && renameSpace(s.space_id)}
                                    autoFocus
                                  />
                                  <button className="text-green-600 hover:text-green-700 p-0.5" onClick={() => renameSpace(s.space_id)} disabled={savingSpace}>
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button className="text-[#737373] hover:text-[#1A3C34] p-0.5" onClick={() => setEditingSpaceName(null)}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="text-[11px] text-[#1A3C34] font-medium flex-1 truncate">{s.name}</span>
                                  <span className="text-[9px] text-[#737373] mr-1">{s.price_hourly}EUR/h</span>
                                  {editingCapacity === s.space_id ? (
                                    <span className="flex items-center gap-0.5 mr-1" data-testid={`edit-capacity-${s.space_id}`}>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={editCapacityValue}
                                        onChange={(e) => setEditCapacityValue(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && saveCapacity(s.space_id)}
                                        className="h-5 w-12 text-[10px] border-[#E2E2DF] px-1"
                                        autoFocus
                                      />
                                      <span className="text-[9px] text-[#737373]">pers.</span>
                                      <button className="text-green-600 hover:text-green-700 p-0.5" onClick={() => saveCapacity(s.space_id)} disabled={savingSpace}>
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button className="text-[#737373] hover:text-[#1A3C34] p-0.5" onClick={() => setEditingCapacity(null)}>
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      className="text-[9px] text-[#737373] mr-1 px-1 py-0.5 rounded hover:bg-[#C77D63]/10 hover:text-[#C77D63] flex items-center gap-0.5 transition-colors"
                                      onClick={() => { setEditingCapacity(s.space_id); setEditCapacityValue(String(s.capacity || 1)); }}
                                      title="Modifier la capacite"
                                      data-testid={`capacity-btn-${s.space_id}`}
                                    >
                                      <Users className="w-2.5 h-2.5" />
                                      {s.capacity || 1} pers.
                                    </button>
                                  )}
                                  <button className="text-[#737373] hover:text-[#4A90D9] p-0.5" onClick={() => { setEditingSpaceName(s.space_id); setEditingNameValue(s.name); }} title="Renommer">
                                    <Edit3 className="w-3 h-3" />
                                  </button>
                                  <button className="text-[#737373] hover:text-[#C77D63] p-0.5" onClick={() => startEditPrices(s)} title="Tarifs et capacite">
                                    <DollarSign className="w-3 h-3" />
                                  </button>
                                  <button className="text-[#737373] hover:text-red-500 p-0.5" onClick={() => deleteSpace(s.space_id)} title="Supprimer">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                            {/* Price editing */}
                            {editingSpacePrices === s.space_id && (
                              <div className="mt-1 bg-white border border-[#E2E2DF] rounded p-1.5 space-y-1">
                                <div className="grid grid-cols-3 gap-1">
                                  <div>
                                    <label className="text-[9px] text-[#737373]">EUR/h</label>
                                    <Input type="number" step="0.5" value={editPriceValues.price_hourly} onChange={(e) => setEditPriceValues(p => ({...p, price_hourly: e.target.value}))} className="h-5 text-[10px] border-[#E2E2DF]" />
                                  </div>
                                  <div>
                                    <label className="text-[9px] text-[#737373]">EUR/j</label>
                                    <Input type="number" step="0.5" value={editPriceValues.price_daily} onChange={(e) => setEditPriceValues(p => ({...p, price_daily: e.target.value}))} className="h-5 text-[10px] border-[#E2E2DF]" />
                                  </div>
                                  <div>
                                    <label className="text-[9px] text-[#737373]">EUR/sem</label>
                                    <Input type="number" step="0.5" value={editPriceValues.price_weekly} onChange={(e) => setEditPriceValues(p => ({...p, price_weekly: e.target.value}))} className="h-5 text-[10px] border-[#E2E2DF]" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="flex-1">
                                    <label className="text-[9px] text-[#737373]">Capacite</label>
                                    <Input type="number" value={editPriceValues.capacity} onChange={(e) => setEditPriceValues(p => ({...p, capacity: e.target.value}))} className="h-5 text-[10px] border-[#E2E2DF]" />
                                  </div>
                                  <Button size="sm" className="h-5 px-2 text-[9px] bg-green-600 text-white hover:bg-green-700 mt-3" onClick={() => savePrices(s.space_id)} disabled={savingSpace}>
                                    <Check className="w-2.5 h-2.5 mr-0.5" /> OK
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] border-[#E2E2DF] mt-3" onClick={() => setEditingSpacePrices(null)}>
                                    <X className="w-2.5 h-2.5" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Add new space */}
                      <div className="pt-1.5 border-t border-[#E2E2DF] space-y-1">
                        <p className="text-[9px] font-semibold text-[#C77D63]">Nouvel espace</p>
                        <Input
                          value={newSpaceName}
                          onChange={(e) => setNewSpaceName(e.target.value)}
                          placeholder="Nom"
                          className="h-6 text-[11px] border-[#E2E2DF]"
                          data-testid="new-space-name-input"
                        />
                        <div className="grid grid-cols-4 gap-1">
                          <div>
                            <label className="text-[9px] text-[#737373]">EUR/h</label>
                            <Input type="number" step="0.5" value={newSpacePrice} onChange={(e) => setNewSpacePrice(e.target.value)} className="h-5 text-[10px] border-[#E2E2DF]" />
                          </div>
                          <div>
                            <label className="text-[9px] text-[#737373]">EUR/j</label>
                            <Input type="number" step="0.5" value={newSpacePriceDaily} onChange={(e) => setNewSpacePriceDaily(e.target.value)} className="h-5 text-[10px] border-[#E2E2DF]" />
                          </div>
                          <div>
                            <label className="text-[9px] text-[#737373]">EUR/sem</label>
                            <Input type="number" step="0.5" value={newSpacePriceWeekly} onChange={(e) => setNewSpacePriceWeekly(e.target.value)} className="h-5 text-[10px] border-[#E2E2DF]" />
                          </div>
                          <div>
                            <label className="text-[9px] text-[#737373]">Capacite</label>
                            <Input type="number" value={newSpaceCapacity} onChange={(e) => setNewSpaceCapacity(e.target.value)} className="h-5 text-[10px] border-[#E2E2DF]" />
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full h-6 text-[10px] bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
                          onClick={addNewSpace}
                          disabled={savingSpace || !newSpaceName.trim()}
                          data-testid="add-new-space-btn"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Creer l'espace
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Image upload */}
              <div>
                <label className="text-xs text-[#737373]">Image</label>
                {selectedEl.image_url ? (
                  <div className="mt-1 relative">
                    <img
                      src={selectedEl.image_url}
                      alt="preview"
                      className="w-full h-20 object-cover rounded border border-[#E2E2DF]"
                    />
                    <div className="flex gap-1 mt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs flex-1 border-[#E2E2DF]"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        <ImagePlus className="w-3 h-3 mr-1" /> Changer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-red-200 text-red-500 hover:bg-red-50"
                        onClick={removeImage}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 text-xs mt-1 border-dashed border-[#E2E2DF]"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="editor-upload-image-btn"
                  >
                    <ImagePlus className="w-3 h-3 mr-1" />
                    {uploadingImage ? 'Upload...' : 'Ajouter une image'}
                  </Button>
                )}
              </div>

              {/* Color */}
              <div>
                <label className="text-xs text-[#737373]">Couleur du fond</label>
                <Select value={selectedEl.color} onValueChange={(v) => updateElement('color', v)}>
                  <SelectTrigger className="h-8 text-sm border-[#E2E2DF] mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: c.value, border: '1px solid #ccc' }} />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Text color */}
              <div>
                <label className="text-xs text-[#737373]">Couleur du texte</label>
                <Select value={selectedEl.text_color || 'auto'} onValueChange={(v) => updateElement('text_color', v)}>
                  <SelectTrigger className="h-8 text-sm border-[#E2E2DF] mt-1" data-testid="editor-text-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEXT_COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          {c.value !== 'auto' ? (
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: c.value, border: '1px solid #ccc' }} />
                          ) : (
                            <div className="w-3 h-3 rounded bg-gradient-to-br from-[#1A3C34] to-[#C77D63]" />
                          )}
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show price toggle (spaces only) */}
              {selectedEl.type === 'space' && selectedEl.linked_space_id && (
                <label className="flex items-center justify-between gap-2 py-1 cursor-pointer" data-testid="editor-show-price-toggle">
                  <span className="text-xs text-[#737373]">Afficher le prix sur l'image</span>
                  <input
                    type="checkbox"
                    checked={selectedEl.show_price !== false}
                    onChange={(e) => updateElement('show_price', e.target.checked)}
                    className="h-4 w-4 accent-[#C77D63] cursor-pointer"
                  />
                </label>
              )}

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[#737373]">Largeur</label>
                  <Input
                    type="number"
                    value={selectedEl.width}
                    onChange={(e) => updateElement('width', parseInt(e.target.value) || 40)}
                    className="h-8 text-sm border-[#E2E2DF] mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#737373]">Hauteur</label>
                  <Input
                    type="number"
                    value={selectedEl.height}
                    onChange={(e) => updateElement('height', parseInt(e.target.value) || 25)}
                    className="h-8 text-sm border-[#E2E2DF] mt-1"
                  />
                </div>
              </div>

              {/* Position */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[#737373]">X</label>
                  <Input
                    type="number"
                    value={selectedEl.x}
                    onChange={(e) => updateElement('x', parseInt(e.target.value) || 0)}
                    className="h-8 text-sm border-[#E2E2DF] mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#737373]">Y</label>
                  <Input
                    type="number"
                    value={selectedEl.y}
                    onChange={(e) => updateElement('y', parseInt(e.target.value) || 0)}
                    className="h-8 text-sm border-[#E2E2DF] mt-1"
                  />
                </div>
              </div>

              {/* Font size */}
              <div>
                <label className="text-xs text-[#737373]">Taille texte</label>
                <Input
                  type="number"
                  value={selectedEl.font_size || 13}
                  onChange={(e) => updateElement('font_size', parseInt(e.target.value) || 13)}
                  className="h-8 text-sm border-[#E2E2DF] mt-1"
                  min={8}
                  max={32}
                />
              </div>

              {/* Help */}
              <div className="pt-2 border-t border-[#E2E2DF] text-xs text-[#737373]">
                <Move className="w-3 h-3 inline mr-1" /> Glissez pour deplacer
                <br />
                <Maximize2 className="w-3 h-3 inline mr-1" /> Coin orange = redimensionner
              </div>
            </>
          )}

          {!selectedEl && (
            <p className="text-xs text-[#737373]">
              Cliquez sur un element du plan pour modifier ses proprietes, ajouter une image, ou le lier a un espace.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
